package uk.co.nickthecoder.glok.backend.webgl

import org.khronos.webgl.*
import org.khronos.webgl.WebGLRenderingContext.Companion.ARRAY_BUFFER
import org.khronos.webgl.WebGLRenderingContext.Companion.COMPILE_STATUS
import org.khronos.webgl.WebGLRenderingContext.Companion.FLOAT
import org.khronos.webgl.WebGLRenderingContext.Companion.FRAGMENT_SHADER
import org.khronos.webgl.WebGLRenderingContext.Companion.LINK_STATUS
import org.khronos.webgl.WebGLRenderingContext.Companion.STATIC_DRAW
import org.khronos.webgl.WebGLRenderingContext.Companion.TRIANGLES
import org.khronos.webgl.WebGLRenderingContext.Companion.VERTEX_SHADER
import uk.co.nickthecoder.glok.scene.Color
import uk.co.nickthecoder.glok.util.Matrix
import uk.co.nickthecoder.glok.util.log
import uk.co.nickthecoder.glok.util.simpleName

internal abstract class Program(
    vertex: String,
    fragment: String,
    val floatsPerVertex: Int
) {
    val program = createProgram(vertex, fragment)

    val positionLocation: Int = getAttributeLocation("position")

    val viewMatrixLocation: WebGLUniformLocation? = getUniformLocation("viewMatrix")


    protected fun getUniformLocation(name: String, warn: Boolean = true): WebGLUniformLocation? {
        if (program == null) return null
        val result = WebGLBackend.gl.getUniformLocation(program, name)
        if (warn && result == null) {
            log.warn("Uniform $name not found in ${simpleName()}")
        }
        return result
    }

    protected fun getAttributeLocation(name: String, warn: Boolean = true): Int {
        val result = WebGLBackend.gl.getAttribLocation(program, name)
        if (warn && result < 0) {
            log.warn("Uniform $name not found in ${simpleName()}")
        }
        return result
    }

    protected fun setUniform(location: WebGLUniformLocation?, value: Color?) {
        val color = value ?: Color.WHITE
        WebGLBackend.gl.uniform4f(location, color.red, color.green, color.blue, color.alpha)
    }

    protected fun setUniform(location: WebGLUniformLocation?, value: Float) {
        WebGLBackend.gl.uniform1f(location, value)
    }

    protected fun setUniform(location: WebGLUniformLocation?, value: Matrix?) {
        val matrix = value ?: Matrix.IDENTITY
        WebGLBackend.gl.uniformMatrix3fv(location, false, matrix.asFlat3x3Array)
    }

    abstract fun enableAttributes()
    abstract fun disableAttributes()

    protected fun begin(viewMatrix: Matrix) {
        with(WebGLBackend.gl) {
            useProgram(program)
            uniformMatrix3fv(viewMatrixLocation, false, viewMatrix.asFlat3x3Array)
        }
    }

    /**
     * Creates a float buffer with the positions (and possibly uv data) for each vertex,
     * draws it, and then deletes the float buffer.
     * It seems that this isn't the usual practice when using GL; usually such buffers are long-lived.
     * Will this extremely frequent allocation and destruction of buffers be slow, or lead to other issues???
     * NOTE. The GLBackend (for JVM platform) uses a single long-lived buffer, whose data is updated for each
     * (batch) of drawing operations. But this isn't supported using WebGL.
     */
    protected fun drawTriangles(data: Array<Float>, mode: Int = TRIANGLES) {

        with(WebGLBackend.gl) {
            val buffer = createBuffer()
            bindBuffer(ARRAY_BUFFER, buffer)
            bufferData(ARRAY_BUFFER, Float32Array(data), STATIC_DRAW)

            enableAttributes()
            drawArrays(mode, 0, data.size / floatsPerVertex)

            bindBuffer(ARRAY_BUFFER, null)
            deleteBuffer(buffer)
        }
    }

    protected open fun end() {
        disableAttributes()
        WebGLBackend.gl.useProgram(null)
    }

    companion object {

        private fun createProgram(vertexSource: String, fragmentSource: String): WebGLProgram? {
            val vertexShader = createShader(vertexSource, VERTEX_SHADER)
            val fragmentShader = createShader(fragmentSource, FRAGMENT_SHADER)
            with(WebGLBackend.gl) {
                val program = createProgram()
                attachShader(program, vertexShader)
                attachShader(program, fragmentShader)
                linkProgram(program)
                deleteShader(vertexShader)
                deleteShader(fragmentShader)

                if (getProgramParameter(program, LINK_STATUS) != true) {
                    log.error("Unable to initialize the shader program: ${getProgramInfoLog(program)}")
                    return null
                }

                return program
            }
        }

        private fun createShader(source: String, type: Int): WebGLShader? {
            with(WebGLBackend.gl) {
                val shader = createShader(type)

                shaderSource(shader, source)
                compileShader(shader)
                if (getShaderParameter(shader, COMPILE_STATUS) != true) {
                    log.error("An error occurred compiling fragment shader: ${getShaderInfoLog(shader)}")
                    log.info("Shader source : \n${source}")
                    return null
                }

                return shader
            }
        }
    }

}

internal abstract class SimpleProgram(vertexCode: String, fragmentCode: String) : Program(vertexCode, fragmentCode, 2) {

    override fun enableAttributes() {
        with(WebGLBackend.gl) {
            vertexAttribPointer(positionLocation, 2, FLOAT, false, 0, 0)
            enableVertexAttribArray(positionLocation)
        }
    }

    override fun disableAttributes() {
        with(WebGLBackend.gl) {
            disableVertexAttribArray(positionLocation)
        }
    }
}

internal abstract class UVProgram(vertexCode: String, fragmentCode: String) : Program(vertexCode, fragmentCode, 4) {

    val uvLocation: Int = getAttributeLocation("uv")

    override fun enableAttributes() {
        with(WebGLBackend.gl) {
            vertexAttribPointer(positionLocation, 2, FLOAT, false, 16, 0)
            vertexAttribPointer(uvLocation, 2, FLOAT, false, 16, 8)
            enableVertexAttribArray(positionLocation)
            enableVertexAttribArray(uvLocation)
        }
    }

    override fun disableAttributes() {
        with(WebGLBackend.gl) {
            disableVertexAttribArray(positionLocation)
            disableVertexAttribArray(uvLocation)
        }
    }
}


private fun Float32Array.toStr(): String {
    val array = this
    return StringBuilder().apply {
        append("[  ")
        for (i in 0 until array.length) {
            append(array[i])
            append("  ")
        }
        append("]")
    }.toString()
}
